/**
 * Custom hook for validating delivery selections
 * Returns validation result with field-level error grouping
 */

import { useMemo } from "react";
import { SelectedDeliveryOption } from "../types";
import {
  ValidationConfig,
  ValidationResult,
  DELIVERY_SELECTION_VALIDATION_RULES,
} from "./deliverySelectionValidation";

export function useDeliverySelectionValidation(
  selection: SelectedDeliveryOption | undefined,
  config: ValidationConfig,
): ValidationResult {
  return useMemo(() => {
    if (!selection) {
      return { isValid: true, errors: [], errorsByField: {} };
    }

    const errors = DELIVERY_SELECTION_VALIDATION_RULES.filter((rule) =>
      rule.shouldApply(selection, config),
    )
      .filter((rule) => !rule.validate(selection, config))
      .map((rule) => ({
        id: rule.id,
        message: rule.getErrorMessage(selection, config),
        targetField: rule.targetField,
      }));

    const errorsByField = errors.reduce(
      (acc, error) => {
        const field = error.targetField || "general";
        acc[field] = acc[field] || [];
        acc[field].push(error);
        return acc;
      },
      {} as Record<string, ValidationResult["errors"]>,
    );

    return {
      isValid: errors.length === 0,
      errors,
      errorsByField,
    };
  }, [selection, config]);
}
